import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout.tsx';

type LegalShellProps = {
  title: string;
  children: ReactNode;
};

const sectionClass = 'space-y-3';
const headingClass = 'text-lg font-black text-slate-900';
const subheadingClass = 'text-sm font-extrabold uppercase tracking-wide text-slate-500';
const paragraphClass = 'text-sm leading-7 text-slate-600';
const listClass = 'space-y-2 text-sm leading-7 text-slate-600';

const Divider = () => <hr className="my-7 border-slate-100" />;

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className={sectionClass}>
    <h2 className={headingClass}>{title}</h2>
    {children}
  </section>
);

const LegalShell = ({ title, children }: LegalShellProps) => (
  <Layout>
    <div className="min-h-full bg-[#f6f7fb] px-3 py-6 text-slate-900 sm:px-6 lg:px-8">
      <div className="mx-auto w-full max-w-5xl">
        <Link
          to="/atendabot"
          className="inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
        >
          <span aria-hidden="true" className="mr-2">&larr;</span>
          Voltar para o AtendaBot
        </Link>

        <header className="mt-6 rounded-[24px] border border-slate-200 bg-white px-6 py-7 text-center shadow-[0_10px_30px_rgba(22,34,66,0.08)]">
          <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-teal-700">
            AtendaBot
          </p>
          <h1 className="mt-3 text-2xl font-black text-slate-900 md:text-3xl">
            {title}
          </h1>
          <p className="mt-2 text-sm font-semibold text-slate-500">
            Última atualização: 05/01/2025
          </p>
        </header>

        <main className="mt-5 rounded-[24px] border border-slate-200 bg-white p-6 shadow-[0_10px_30px_rgba(22,34,66,0.08)] md:p-8">
          {children}
        </main>
      </div>
    </div>
  </Layout>
);

export const AtendabotTermsOfUse = () => (
  <LegalShell title="Termos de Uso">
    <Section title="1. Identificação das Partes">
      <h3 className={subheadingClass}>Controladora e Responsável pela Plataforma</h3>
      <p className={paragraphClass}>
        GOURMEX TECNOLOGIA E MARKETING LTDA, pessoa jurídica de direito privado, inscrita no
        CNPJ sob nº 51.555.314/0001-91, com sede na Rua Dra. Rita Lobato, nº 194, Apto 202,
        Andar 2, Bairro Praia de Belas, Porto Alegre/RS, CEP 90110-040, doravante denominada
        “GOURMEX”.
      </p>
      <h3 className={subheadingClass}>Plataforma</h3>
      <p className={paragraphClass}>
        AtendaBot é uma solução tecnológica pertencente ao Grupo Gourmex, operada e mantida
        pela GOURMEX, não possuindo personalidade jurídica própria.
      </p>
      <h3 className={subheadingClass}>Usuário / Contratante</h3>
      <p className={paragraphClass}>
        Pessoa física ou jurídica que realiza cadastro e utiliza a solução AtendaBot, doravante
        denominada “Usuário” ou “Cliente”.
      </p>
    </Section>

    <Divider />

    <Section title="2. Definições">
      <ul className={listClass}>
        <li>2.1. Plataforma: Ecossistema digital AtendaBot, acessível via web.</li>
        <li>2.2. Soluções: Conjunto de módulos e funcionalidades disponibilizadas pela Plataforma.</li>
        <li>2.3. Conta: Cadastro individual do Usuário.</li>
        <li>2.4. Plano: Modalidade de assinatura contratada.</li>
        <li>2.5. Dados do Cliente: Informações inseridas pelo Usuário na Plataforma.</li>
        <li>2.6. Dados Pessoais: Dados conforme definição da Lei nº 13.709/2018 (LGPD).</li>
        <li>2.7. IA: Funcionalidades de inteligência artificial utilizadas para automação, sugestões e análises.</li>
      </ul>
    </Section>

    <Divider />

    <Section title="3. Objeto">
      <p className={paragraphClass}>
        A GOURMEX concede ao Usuário uma licença de uso não exclusiva, não transferível,
        revogável e limitada, para acesso e utilização da solução AtendaBot, conforme o Plano
        contratado, exclusivamente para fins internos da operação do restaurante.
      </p>
    </Section>

    <Divider />

    <Section title="4. Aceite Eletrônico">
      <p className={paragraphClass}>O aceite destes Termos ocorre mediante:</p>
      <ul className={listClass}>
        <li>a) Cadastro na Plataforma;</li>
        <li>b) Marcação de checkbox de concordância;</li>
        <li>c) Confirmação de pagamento;</li>
        <li>d) Primeiro acesso à Plataforma.</li>
      </ul>
      <p className={paragraphClass}>
        O aceite eletrônico equivale juridicamente à assinatura, nos termos da legislação vigente.
      </p>
    </Section>

    <Divider />

    <Section title="5. Planos, Cobrança e Cancelamento">
      <ul className={listClass}>
        <li>5.1. A Plataforma opera por modelo de assinatura, com renovação automática.</li>
        <li>5.2. O Usuário pode cancelar a qualquer momento, mantendo acesso até o final do período pago.</li>
        <li>5.3. Não há reembolso de valores pagos, salvo obrigação legal ou cobrança indevida.</li>
        <li>5.4. A GOURMEX poderá revisar valores mediante aviso prévio mínimo de 30 dias.</li>
      </ul>
    </Section>

    <Divider />

    <Section title="6. Uso da Plataforma e Inteligência Artificial">
      <ul className={listClass}>
        <li>6.1. O AtendaBot fornece ferramentas de apoio à operação, não garantindo resultados financeiros ou operacionais.</li>
        <li>6.2. As funcionalidades de IA são auxiliares e não substituem análise humana.</li>
        <li>6.3. O Usuário é responsável por revisar conteúdos, mensagens e decisões geradas com apoio da IA.</li>
      </ul>
      <p className={paragraphClass}>
        A GOURMEX não se responsabiliza por bloqueios, penalidades, suspensões ou sanções
        aplicadas por terceiros, incluindo provedores de mensagens, plataformas de comunicação
        ou redes sociais.
      </p>
    </Section>

    <Divider />

    <Section title="7. Obrigações do Usuário">
      <p className={paragraphClass}>O Usuário compromete-se a:</p>
      <ul className={listClass}>
        <li>a) Utilizar a Plataforma de forma lícita;</li>
        <li>b) Garantir base legal para contato com clientes finais;</li>
        <li>c) Não praticar spam ou comunicações indevidas;</li>
        <li>d) Manter confidencialidade de suas credenciais;</li>
        <li>e) Ser responsável pelos dados e conteúdos inseridos.</li>
      </ul>
    </Section>

    <Divider />

    <Section title="8. Propriedade Intelectual">
      <ul className={listClass}>
        <li>8.1. A solução AtendaBot, seu código, design, marcas e tecnologia são de propriedade exclusiva da GOURMEX.</li>
        <li>8.2. Os dados e conteúdos inseridos pelo Usuário permanecem sob sua titularidade.</li>
        <li>8.3. A GOURMEX não utiliza dados do Cliente para treinar modelos de IA de terceiros, salvo consentimento expresso.</li>
      </ul>
    </Section>

    <Divider />

    <Section title="9. Limitação de Responsabilidade">
      <p className={paragraphClass}>
        A responsabilidade máxima da GOURMEX fica limitada ao valor total pago pelo Usuário
        nos últimos 12 (doze) meses.
      </p>
      <p className={paragraphClass}>
        Em nenhuma hipótese haverá responsabilidade por danos indiretos, lucros cessantes,
        perda de dados, decisões automatizadas ou falhas de terceiros.
      </p>
    </Section>

    <Divider />

    <Section title="10. Alterações dos Termos">
      <p className={paragraphClass}>
        A GOURMEX poderá alterar estes Termos a qualquer momento, mediante publicação da
        versão atualizada. A continuidade do uso da Plataforma implica aceitação das
        alterações.
      </p>
    </Section>

    <Divider />

    <Section title="11. Lei Aplicável e Foro">
      <p className={paragraphClass}>Aplica-se a legislação brasileira.</p>
      <p className={paragraphClass}>Fica eleito o foro da comarca de Porto Alegre/RS, ressalvados os direitos do consumidor.</p>
    </Section>
  </LegalShell>
);

export const AtendabotPrivacyPolicy = () => (
  <LegalShell title="Política de Privacidade">
    <Section title="1. Introdução">
      <p className={paragraphClass}>
        O AtendaBot, solução pertencente ao Grupo Gourmex e operada pela GOURMEX
        TECNOLOGIA E MARKETING LTDA, respeita a privacidade e a proteção dos dados pessoais,
        tratando-os conforme a Lei Geral de Proteção de Dados (Lei nº 13.709/2018).
      </p>
    </Section>

    <Divider />

    <Section title="2. Agente de Tratamento">
      <p className={paragraphClass}>
        A GOURMEX atua como Controladora ou Operadora de Dados, conforme a natureza do
        tratamento realizado.
      </p>
    </Section>

    <Divider />

    <Section title="3. Dados Coletados">
      <ul className={listClass}>
        <li>3.1. Dados cadastrais do Usuário.</li>
        <li>3.2. Dados operacionais inseridos na Plataforma.</li>
        <li>3.3. Dados de navegação e logs de acesso.</li>
        <li>3.4. Dados de clientes finais inseridos pelo restaurante.</li>
      </ul>
    </Section>

    <Divider />

    <Section title="4. Finalidade do Tratamento">
      <p className={paragraphClass}>Os dados são tratados para:</p>
      <ul className={listClass}>
        <li>a) Prestação dos serviços;</li>
        <li>b) Suporte e comunicação;</li>
        <li>c) Segurança e prevenção de fraudes;</li>
        <li>d) Cumprimento de obrigações legais;</li>
        <li>e) Melhoria contínua da Plataforma.</li>
      </ul>
    </Section>

    <Divider />

    <Section title="5. Inteligência Artificial">
      <ul className={listClass}>
        <li>5.1. Dados não são utilizados para treinar modelos de IA de terceiros.</li>
        <li>5.2. A IA atua como ferramenta auxiliar.</li>
        <li>5.3. O Usuário é responsável por validar conteúdos e decisões.</li>
      </ul>
    </Section>

    <Divider />

    <Section title="6. Compartilhamento de Dados">
      <p className={paragraphClass}>Os dados podem ser compartilhados apenas com prestadores essenciais, como:</p>
      <ul className={listClass}>
        <li>a) Infraestrutura em nuvem;</li>
        <li>b) Processadores de pagamento;</li>
        <li>c) Serviços de suporte e monitoramento.</li>
      </ul>
      <p className={paragraphClass}>A GOURMEX não vende dados pessoais.</p>
    </Section>

    <Divider />

    <Section title="7. Segurança da Informação">
      <p className={paragraphClass}>
        Adotamos medidas técnicas e organizacionais adequadas para proteção dos dados,
        incluindo controle de acesso, criptografia e monitoramento.
      </p>
      <p className={paragraphClass}>Nenhum sistema é totalmente imune a riscos.</p>
    </Section>

    <Divider />

    <Section title="8. Retenção e Exclusão">
      <p className={paragraphClass}>Os dados são mantidos:</p>
      <ul className={listClass}>
        <li>a) Durante a vigência da conta;</li>
        <li>b) Pelo prazo legal necessário após o cancelamento.</li>
      </ul>
      <p className={paragraphClass}>Após esse período, os dados são eliminados ou anonimizados.</p>
    </Section>

    <Divider />

    <Section title="9. Direitos do Titular">
      <p className={paragraphClass}>O titular pode solicitar:</p>
      <ul className={listClass}>
        <li>a) Acesso;</li>
        <li>b) Correção;</li>
        <li>c) Exclusão;</li>
        <li>d) Portabilidade;</li>
        <li>e) Revogação de consentimento.</li>
      </ul>
    </Section>

    <Divider />

    <Section title="10. Canais de Contato">
      <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4 text-sm leading-7 text-slate-600">
        <p>GOURMEX TECNOLOGIA E MARKETING LTDA</p>
        <p>CNPJ: 51.555.314/0001-91</p>
        <p>
          Endereço: Rua Dra. Rita Lobato, 194, Apto 202, Andar 2, Praia de Belas, Porto
          Alegre/RS, CEP 90110-040
        </p>
        <p>E-mail de suporte: contato@foodstation.com.br</p>
      </div>
    </Section>

    <Divider />

    <Section title="11. Alterações Desta Política">
      <p className={paragraphClass}>
        Esta Política poderá ser atualizada a qualquer tempo. O uso contínuo da Plataforma
        implica concordância com a versão vigente.
      </p>
    </Section>
  </LegalShell>
);
